import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Box, Button, Checkbox, Chip, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TablePagination, Tab, Tabs, TextField, Typography } from '@mui/material';
import { Icon, InputWithIcon, toast } from '@livable/saas-react-components';
import useStyles from './styles';
import { useStore } from 'src/stores';
import { isLivableStaffFromToken } from 'src/utils/isLivableStaffFromToken';
import { billsPageCompanyId, billsPageCompanyName } from './billsPageCompany';
import { BILL_LINK_ACTIONS, billLinkAction, goToCompanySwitch, sessionCompanyIdFromToken } from './billLinkCompany';
import { BillActionsMenu } from './BillActionsMenu';
import { RetryBillModal } from './RetryBillModal';
import { ConfirmActionModal } from './BillDetails/ConfirmActionModal';
import { BillsFilterPanel } from './BillsFilterPanel';
import { BillsFilterChips } from './BillsFilterChips';
import { BillsHeaderFilterButton } from './BillsHeaderFilterButton';
import { BillsSummaryCards } from './BillsSummaryCards';
import { BillBatchFilterBanner } from './BillBatchFilterBanner';
import { AttachBillModal } from './AttachBillModal';
import { UploadBillModal } from './UploadBillModal';
import { BillIntakeAliasModal } from 'src/pages/AdminPage/BillIntakeAliasModal';
import { UploadedBillDetailsModal } from './UploadedBillDetailsModal';
import { BillNoteIndicator } from './BillNoteIndicator';
import { BILL_STATUSES, DASH, getPropertyAddressIndex, getSelectionDeleteHint, mapCompanyBillToRow } from './companyBillRows';
import { getArchivedNotErasedNote, getDeleteConfirmation } from './archivedAllocationWording';
import { BILL_TABS, UTILITY_FILTER_OPTIONS, activeTabKey, billsQueryFromUrl, billsQueryKey, billsQueryReducer, filtersFromUrl, filtersUrlKey, isAllocationTab, rowMatchesStatuses, tabFilters, toBillsRequestParams, withFiltersInUrl } from './billsQuery';
import { filterChips, pickedValues, withCategoryCleared } from './billsFilterCategories';
import filterPanelStyles from './billsFilterPanelStyles';
import { summaryToTabCounts } from './billsSummary';
import { getAppfolioPushChipProps, getAppfolioPushMenuHint } from './appfolioPushRow';
import { useBillsAppfolioPush } from './useBillsAppfolioPush';
import { useBillsListPoll } from './useBillsListPoll';
import { useBillReadyDates } from './useBillReadyDates';
import { BillsDownloadMenu } from './BillsDownloadMenu';
import { billDownloadBlockedReason, downloadDisabledReason, downloadScopeLabel, pageProcessedBillIds } from './billsDownload';
import { useBillsDownload } from './useBillsDownload';
import { useBillsExportLink } from './useBillsExportLink';
import { useMonthInProgress } from './useMonthInProgress';
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];
const POLL_INTERVAL_MS = 5000;
/** Long enough that a word is typed before it is asked for, short enough not to feel held back. */
const SEARCH_DEBOUNCE_MS = 350;

/** One email's bills (LB-5828) are counted from the rows in hand, so only the tabs that narrow by status are offered. */
const BATCH_TABS = BILL_TABS.filter(tab => !isAllocationTab(tab));

/** The column funnels (LB-6087), by the filter each opens the panel at. */
const HEADER_FILTER_LABELS = {
  statuses: 'Filter by status',
  providers: 'Filter by service provider',
  properties: 'Filter by property',
  allocationStates: 'Filter by allocation',
  apPushEnrolment: 'Filter by AP Push',
  utilities: 'Filter by utility',
  servicePeriod: 'Filter by service period'
};
function formatUsd(amount) {
  if (typeof amount !== 'number' || Number.isNaN(amount)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
}

/** Same HTML for every plain-text table cell: div.MuiTypography (body2, primary). */
function BillCellText(_ref) {
  let children = _ref.children,
    className = _ref.className,
    _ref$noWrap = _ref.noWrap,
    noWrap = _ref$noWrap === void 0 ? false : _ref$noWrap,
    title = _ref.title,
    sx = _ref.sx;
  const nowrapSx = noWrap ? {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    wordBreak: 'normal',
    overflowWrap: 'normal',
    maxWidth: '100%',
    minWidth: 0
  } : {};
  return /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.primary",
    component: "div",
    className: className,
    noWrap: noWrap,
    title: title,
    sx: _objectSpread(_objectSpread({}, nowrapSx), sx)
  }, children);
}

/** Which tone the status pill is drawn in. Keys into the `billsStatus_*` rules. */
function billStatusTone(status) {
  switch (status) {
    case BILL_STATUSES.validated:
      return 'success';
    case BILL_STATUSES.processing:
    case BILL_STATUSES.extracted:
      return 'info';
    case BILL_STATUSES.needsProperty:
    // Rejected shares the warning tone with Needs review because it shares the meaning: this one is
    // waiting on a decision from you. Not 'error' -- nothing failed, we declined it, and the PM can
    // reverse that by approving the sender.
    case BILL_STATUSES.rejected:
      return 'warning';
    // Duplicate is neutral on purpose: it is informational, there is nothing to do, and colouring it
    // would pull the eye to the one row in the list that needs no attention.
    case BILL_STATUSES.duplicate:
      return 'neutral';
    case BILL_STATUSES.failed:
      return 'error';
    default:
      return 'neutral';
  }
}
export const BillsTab = observer(() => {
  var _store$companyBills$l, _summaryToTabCounts, _store$companyBills$f, _store$companyBills$f2, _store$companyBills$f3, _selectedRows$, _paginatedRows$find, _store$companyBills$g, _store$companyBills$g2, _store$companyBills$g3, _download$dialog$conf, _download$dialog, _download$dialog$mess, _download$dialog2, _download$dialog$titl, _download$dialog3;
  const classes = useStyles();
  const filterClasses = filterPanelStyles();
  const store = useStore();
  /**
   * From the token, not `store.account.userProfile.isUserLivableStaff`.
   *
   * That view reads the localStorage mirror, which on a cold load is written after the first
   * render and is not observable, so a gate keyed on it settles on "not staff" and never re-runs.
   * It cost the admin page its staff tabs once already (LB-5770). It did not matter here while the
   * button merely rendered disabled; now that it is hidden outright (LB-5872) the same staleness
   * would take it away from staff entirely until they navigated off the page and back.
   */
  const isUserLivableStaff = isLivableStaffFromToken();
  // The settings modal manages the same company the banner above shows; billsPageCompany says why (LB-5903).
  const intakeCompanyId = billsPageCompanyId(store);
  const hasIntakeCompany = !!intakeCompanyId;
  const intakeCompanyName = billsPageCompanyName(store, intakeCompanyId);
  const isAppfolio = store.company.companyFeatureStore.isAppfolio;
  /**
   * The flag gates the trigger only — push status is readable either way, and a bill pushed before the
   * flag was turned off still says so. So the column reads for every AppFolio company; only the action
   * behind it waits on the flag.
   */
  const isApPushEnabled = isAppfolio && store.account.featureFlagsStore.getIsPushUtilityBillAppFolioEnabled;
  // The customer's note on a row, only where notes are on (LB-6056).
  const areNotesShown = store.account.featureFlagsStore.getIsBillNotesEnabled;
  const addressById = getPropertyAddressIndex(store.company.propertiesList);
  const monthInProgress = useMonthInProgress();
  const rows = store.companyBills.items.map(bill => mapCompanyBillToRow(bill, addressById, monthInProgress));
  const uploadsInFlight = store.bulkUpload.inFlightCount;

  /**
   * Everything the list is asked for, in one place. Page, size, sort, tab, search and filters used to
   * be six pieces of state that each had to remember to reset the page, and one of them -- sorting --
   * did not, so sorting from page four asked the server for page four of a freshly ordered list.
   */
  const _useSearchParams = useSearchParams(),
    _useSearchParams2 = _slicedToArray(_useSearchParams, 2),
    searchParams = _useSearchParams2[0],
    setSearchParams = _useSearchParams2[1];
  // Every filter starts from the address (LB-6087; allocation and AP Push since LB-6054 and LB-6031), so the first
  // read is already the filtered one.
  const _useReducer = useReducer(billsQueryReducer, searchParams, billsQueryFromUrl),
    _useReducer2 = _slicedToArray(_useReducer, 2),
    query = _useReducer2[0],
    dispatchQuery = _useReducer2[1];
  const order = query.order,
    orderBy = query.orderBy,
    page = query.page,
    rowsPerPage = query.pageSize,
    filters = query.filters;
  // The filter panel's state; what each field is for is said where it is opened, below.
  const _useState = useState({
      anchorEl: null,
      categoryKey: null,
      openedFrom: null,
      openId: 0
    }),
    _useState2 = _slicedToArray(_useState, 2),
    filterPanel = _useState2[0],
    setFilterPanel = _useState2[1];

  /**
   * And the two are kept the same both ways: a change here goes into the address, so a reload or a shared link lands
   * on the same list; a change of address without leaving the page -- the Bills link in the menu, another shared link,
   * Back -- comes back here. Both sides are compared in one canonical form, since the address and a pick can name the
   * same values in another order, so neither answers the other's change with one of its own.
   */
  const filtersKey = filtersUrlKey(filters);
  const addressFilters = filtersFromUrl(searchParams);
  const addressFiltersKey = filtersUrlKey(addressFilters);
  /**
   * How the next filter change reaches the address. One a person made (Apply, a tab, a chip's ×, Clear all) is a step
   * of its own, so Back undoes it; one the page made (a batch link's hint becoming filters) replaces the entry it came
   * from, or Back would land on the hint and it would apply itself again.
   */
  const filterHistoryRef = useRef('replace');

  // Every filter in one write: "Clear all" changes several at once, and a write per filter would each start from the
  // address as it was, so each would put back what the one before it cleared.
  //
  // A batch link's `tab` hint (LB-5828) goes in the same write. By now it has become filters; left in the address, a
  // reload would apply it again over whatever was picked since.
  useEffect(() => {
    if (addressFiltersKey === filtersKey) return;

    // An address still carrying a one-time link (an export to start, a bill to open, a company to switch to) is
    // replaced rather than kept behind a new step: Back to it would start the export or open the bill again.
    const isOneTimeLink = ['export', 'billId', 'companyId'].some(name => searchParams.has(name));
    const replace = filterHistoryRef.current !== 'push' || isOneTimeLink;
    filterHistoryRef.current = 'replace';
    setSearchParams(current => {
      const next = withFiltersInUrl(current, filters);
      next.delete('tab');
      return next;
    }, {
      replace
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);
  useEffect(() => {
    if (addressFiltersKey === filtersKey) return;
    dispatchQuery({
      type: 'SET_FILTERS',
      filters: addressFilters
    });
    // Back or a link moved the filters under an open panel; its draft no longer starts from what the list shows.
    setFilterPanel(current => current.anchorEl ? _objectSpread(_objectSpread({}, current), {}, {
      anchorEl: null
    }) : current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressFiltersKey]);

  /**
   * The filter panel (LB-6087): where it is anchored, which filter it opens at, and what opened it. A funnel anchors it to
   * itself. "+ Filter" and a chip anchor it to "+ Filter", which never unmounts: a chip does, once its filter is cleared,
   * and a panel left anchored to it jumps to the corner. Each opening has its own id, so its draft starts afresh.
   *
   * Picks are a draft until Apply, kept in the panel (BillsFilterPanel). Each pick used to read the list again, so four
   * picks were four reads, and an allocation filter makes every one of them a read of the whole allocation book.
   */
  const addFilterButtonRef = useRef(null);
  const handleOpenFilterPanel = function (anchorEl) {
    let categoryKey = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    let openedFrom = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'toolbar';
    return setFilterPanel(current => ({
      anchorEl,
      categoryKey,
      openedFrom,
      openId: current.openId + 1
    }));
  };
  const handleCloseFilterPanel = () => setFilterPanel(current => _objectSpread(_objectSpread({}, current), {}, {
    anchorEl: null
  }));
  const isFilterPanelOpenFrom = from => Boolean(filterPanel.anchorEl) && filterPanel.openedFrom === from;
  const isAnchoredAtAddFilter = isFilterPanelOpenFrom('toolbar') || isFilterPanelOpenFrom('chip');
  const setFilters = function (next) {
    let history = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'push';
    filterHistoryRef.current = history;
    dispatchQuery({
      type: 'SET_FILTERS',
      filters: next
    });
  };
  const handleClearAllFilters = () => {
    filterHistoryRef.current = 'push';
    dispatchQuery({
      type: 'CLEAR_FILTERS'
    });
  };
  const handleApplyFilters = draft => {
    setFilters(draft);
    handleCloseFilterPanel();
  };

  /**
   * Selected bills, held as the rows themselves rather than as ids.
   *
   * An id resolved against the loaded rows worked only because every row was loaded. Over one page
   * it silently drops a selection made on another -- and `.filter(Boolean)` made that silent in the
   * worst place: a bulk delete confirmed "Delete 5" and deleted the three still on screen.
   */
  const _useState3 = useState({}),
    _useState4 = _slicedToArray(_useState3, 2),
    selectedById = _useState4[0],
    setSelectedById = _useState4[1];
  const selected = Object.keys(selectedById);

  // What the input holds, which is not what has been asked for yet. Kept apart so typing never lags
  // behind the request and the field cannot jump under somebody mid-word.
  const _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    searchInput = _useState6[0],
    setSearchInput = _useState6[1];

  /**
   * Bill and batch links from the emails name the company they were sent for (LB-6046). Decided before
   * anything opens: a link for this company opens as before; Livable staff on another company switch
   * first; anyone else is told the bill is another company's instead of watching it fail to load.
   */
  const deepLinkCompanyId = searchParams.get('companyId');
  const linkAction = billLinkAction({
    linkCompanyId: deepLinkCompanyId,
    sessionCompanyId: sessionCompanyIdFromToken(),
    selectedCompanyId: billsPageCompanyId(store),
    isStaff: isLivableStaffFromToken()
  });
  const isLinkForThisCompany = linkAction === BILL_LINK_ACTIONS.open;
  /**
   * Deep link (LB-5828): the batch-summary CTA lands on /bills?batchId={id}&tab={hint}. Read here,
   * above the list fetch, so the fetch itself is scoped to the event server-side — the whole batch
   * comes back regardless of how recent it is, instead of a client-side filter over just the first
   * page of the company-wide list. `null` (no batchId) fetches the whole company as before.
   */
  const deepLinkBatchId = isLinkForThisCompany ? searchParams.get('batchId') : null;
  const deepLinkTab = isLinkForThisCompany ? searchParams.get('tab') : null;
  useEffect(() => {
    if (linkAction === BILL_LINK_ACTIONS.switchCompany) {
      goToCompanySwitch(deepLinkCompanyId);
      return;
    }
    if (!deepLinkCompanyId) return;

    // Acted on once. Left in the address, a refresh or a later close would switch or warn all over again.
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('companyId');
    if (linkAction === BILL_LINK_ACTIONS.otherCompany) {
      ['billId', 'batchId', 'tab', 'export'].forEach(key => nextParams.delete(key));
      toast('warning', {
        title: 'This link is for another company',
        body: ["It's for a company you're not signed in to, so it can't be opened here."]
      });
    }
    setSearchParams(nextParams, {
      replace: true
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkAction, deepLinkCompanyId]);

  /** Moved on by a Refresh, or a push or an attach from the table, which wakes a list poll that gave up. */
  const _useState7 = useState(0),
    _useState8 = _slicedToArray(_useState7, 2),
    pollRestarts = _useState8[0],
    setPollRestarts = _useState8[1];
  /** Bumped when the open bill re-enters the pipeline (entered by hand, LB-6095), so its own read starts again. */
  const _useState9 = useState(0),
    _useState0 = _slicedToArray(_useState9, 2),
    detailReads = _useState0[0],
    setDetailReads = _useState0[1];
  const appfolioPush = useBillsAppfolioPush({
    isEnabled: isAppfolio,
    // A queued push shows up as the row turning pending, which is what arms the list poll.
    onPushed: () => {
      store.companyBills.refetchCurrent();
      setPollRestarts(count => count + 1);
    }
  });
  const canTriggerPush = isApPushEnabled && appfolioPush.isVisible;

  /**
   * One read per view. The key is the request itself, so the list is re-read when what it is asked
   * for changes and not when it has not -- a date that moved by a millisecond used to be enough.
   */
  // AP Push is AppFolio's (LB-6031): a link carrying it means nothing to another company, which has no control to clear it.
  const requestQuery = isAppfolio || !filters.apPushEnrolment ? query : _objectSpread(_objectSpread({}, query), {}, {
    filters: _objectSpread(_objectSpread({}, filters), {}, {
      apPushEnrolment: ''
    })
  });
  const queryKey = billsQueryKey(requestQuery);
  // The key IS the dependency, deliberately. `query` is a new object on every render, so depending
  // on it would re-read the list on every one — and the key is built from the request itself, so it
  // moves exactly when what is being asked for does.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const requestParams = useMemo(() => toBillsRequestParams(requestQuery), [queryKey]);

  // Every view can be asked for now: the tabs set the Status filter rather than being ANDed with it out of sight, so
  // there is no longer a tab and a status that cannot both be satisfied (LB-6087).
  useEffect(() => {
    store.companyBills.getCompanyBillsAsync.run(requestParams, []);
  }, [requestParams, store.companyBills]);
  useEffect(() => {
    // Whole-company figures, so the batch scope does not narrow them: the tiles describe the account
    // while the banner beneath them describes the one event being looked at.
    store.companyBills.getCompanyBillsSummaryAsync.run(undefined, []);
    store.companyBills.getBillFilterOptionsAsync.run(undefined, []);
  }, [store.companyBills]);

  // The URL is where a batch link lives, so the query follows it rather than the other way round.
  useEffect(() => {
    dispatchQuery({
      type: 'SET_BATCH',
      batchId: deepLinkBatchId || null
    });
  }, [deepLinkBatchId]);

  /**
   * Anything that moves a bill between statuses moves a tile with it. Without this an attach takes a
   * bill out of "Needs review" in the table while the tile above still counts it, which reads as the
   * attach having failed.
   */
  const refreshSummary = () => store.companyBills.getCompanyBillsSummaryAsync.run(undefined, []);
  const _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    isUploadBillModalOpen = _useState10[0],
    setIsUploadBillModalOpen = _useState10[1];
  const _useState11 = useState(null),
    _useState12 = _slicedToArray(_useState11, 2),
    selectedBillId = _useState12[0],
    setSelectedBillId = _useState12[1];
  const _useState13 = useState(null),
    _useState14 = _slicedToArray(_useState13, 2),
    attachBillId = _useState14[0],
    setAttachBillId = _useState14[1];
  const _useState15 = useState(null),
    _useState16 = _slicedToArray(_useState15, 2),
    rowToDelete = _useState16[0],
    setRowToDelete = _useState16[1];
  const _useState17 = useState(null),
    _useState18 = _slicedToArray(_useState17, 2),
    rowToRetry = _useState18[0],
    setRowToRetry = _useState18[1];
  const _useState19 = useState(false),
    _useState20 = _slicedToArray(_useState19, 2),
    isConfirmingBulkDelete = _useState20[0],
    setIsConfirmingBulkDelete = _useState20[1];
  // LB-5810: whether the staff-only bill-intake-address modal is open (false = closed).
  const _useState21 = useState(false),
    _useState22 = _slicedToArray(_useState21, 2),
    isIntakeSettingsOpen = _useState22[0],
    setIsIntakeSettingsOpen = _useState22[1];
  const selectedBill = selectedBillId ? store.uploadedBills.bills.find(bill => bill.billId === selectedBillId) : null;
  useBillsListPoll({
    isPaused: !!selectedBillId,
    batchId: deepLinkBatchId,
    restarts: pollRestarts
  });

  /**
   * The one bill being looked at, read as soon as it is opened — the list carries no extraction
   * payload, and waiting for the next pass over the whole list to fetch it made opening the view
   * feel slow. It is read again while the pipeline still has the bill, and left alone once it lands.
   */
  useEffect(() => {
    if (!selectedBillId) return undefined;
    let isActive = true;
    let timeoutId = null;
    const readBill = async () => {
      await store.uploadedBills.pollUploadedBillAsync.run({
        billId: selectedBillId
      });
      if (isActive && store.uploadedBills.isWorthReadingAgain(selectedBillId)) {
        timeoutId = setTimeout(readBill, POLL_INTERVAL_MS);
      }
    };
    readBill();
    return () => {
      isActive = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [selectedBillId, store.uploadedBills, detailReads]);

  /**
   * Deep link: notification emails point at /bills?billId={id}. The bill is registered in the
   * session store the same way "View details" does, and the details effect above then reads it by
   * id — so the link works even before the list has loaded the row. Closing the details view clears
   * the param, so a refresh shows the list rather than reopening the bill.
   */
  const deepLinkBillId = isLinkForThisCompany ? searchParams.get('billId') : null;
  useEffect(() => {
    if (!deepLinkBillId) return;
    if (!store.uploadedBills.bills.some(bill => bill.billId === deepLinkBillId)) {
      store.uploadedBills.addUploadedBill({
        billId: deepLinkBillId
      });
    }
    setSelectedBillId(deepLinkBillId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkBillId]);

  /**
   * The link in a bills download email (LB-6033): /bills?export={id}. The download is asked for again and started,
   * then the link leaves the address. Built from the address as it is when the download lands, not as it was when it
   * was asked for: the router hands a functional update the address of the render that made it, so a filter picked
   * while the download was on its way (LB-6087 keeps them all in the address) would be dropped, and the page would
   * then follow the address and drop it too.
   */
  const latestSearchParamsRef = useRef(searchParams);
  latestSearchParamsRef.current = searchParams;
  useBillsExportLink({
    companyId: billsPageCompanyId(store),
    exportId: isLinkForThisCompany ? searchParams.get('export') : null,
    onHandled: () => {
      const nextParams = new URLSearchParams(latestSearchParamsRef.current);
      nextParams.delete('export');
      setSearchParams(nextParams, {
        replace: true
      });
    }
  });

  /**
   * The `tab` hint (LB-5828) opens the right top tab when it names one we have, by setting the filters that tab stands
   * for. `batchId`/`deepLinkTab` themselves are derived above (they gate the scoped fetch).
   */
  useEffect(() => {
    const hinted = deepLinkTab ? tabFilters(deepLinkTab) : null;
    if (hinted) setFilters(hinted, 'replace');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkTab]);

  /**
   * "Show all bills": drop the batch scope AND the tab hint, and return the tab to "All". Clearing
   * only batchId would leave the list pinned to the hinted subset, so the button would not actually
   * return to the full list. The address and the filters change in one go, so neither reads the
   * other half-done.
   */
  const handleClearBatchFilter = () => {
    const allBills = _objectSpread(_objectSpread({}, filters), tabFilters('all'));
    const nextParams = withFiltersInUrl(searchParams, allBills);
    nextParams.delete('batchId');
    nextParams.delete('tab');
    setSearchParams(nextParams, {
      replace: true
    });
    setFilters(allBills, 'replace');
  };

  /**
   * Open any bill's details by id — how a Duplicate's "view the original" jumps. Registered in the
   * session store like the deep link, so the details poll can fetch it even when this session never
   * uploaded it.
   */
  const handleOpenBillById = billId => {
    if (!store.uploadedBills.bills.some(bill => bill.billId === billId)) {
      store.uploadedBills.addUploadedBill({
        billId
      });
    }
    setSelectedBillId(billId);
  };

  /**
   * The list endpoint carries no extraction payload, so the details view is backed by the session
   * store. A bill that was not uploaded in this session is registered here and read on opening.
   */
  const handleViewDetails = row => {
    if (!store.uploadedBills.bills.some(bill => bill.billId === row.id)) {
      store.uploadedBills.addUploadedBill({
        billId: row.id,
        fileName: row.fileName,
        propertyAddress: (row.propertiesLines || [])[0] || ''
      });
    }
    setSelectedBillId(row.id);
  };

  /** Whatever came of the visit — an allocation, an edit — shows on the row once the list is reread. */
  const handleCloseDetails = () => {
    setSelectedBillId(null);
    if (searchParams.has('billId')) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('billId');
      setSearchParams(nextParams, {
        replace: true
      });
    }
    store.companyBills.refetchCurrent();
    refreshSummary();
  };

  // Batch deep-link scope (LB-5828). The fetch above is server-scoped to the batch, so once the load
  // for the current scope has landed, `rows` IS that event's bills. Until then — the window between a
  // batchId change (including clearing it) and its refetch installing, and after a refetch that failed
  // — the loaded rows still belong to the previous scope, so show nothing rather than present one
  // scope's rows as another's. `loadedBatchId` is the scope the store's items actually came from.
  const loadedBatchId = (_store$companyBills$l = store.companyBills.loadedBatchId) !== null && _store$companyBills$l !== void 0 ? _store$companyBills$l : null;
  // The rows on screen answer the query that fetched them. While a batch scope is changing they
  // still answer the previous one, and presenting one acquisition event's bills as another's is the
  // one staleness worth blanking for -- unlike a page or a filter changing, where holding the old
  // rows for a moment is better than a flicker.
  const scopeSynced = loadedBatchId === (deepLinkBatchId || null);
  const scopedRows = scopeSynced ? rows : [];

  /**
   * Tab counts.
   *
   * Off the summary, which counts the whole company, so they are right past the page the table
   * happens to hold. Under a batch deep link they would be wrong in a different way -- describing
   * the company while the tabs describe one acquisition event -- so that scope counts the rows in
   * hand, which is every bill of the event: a batch is what one email or upload carried, and it is
   * read whole for exactly this reason.
   *
   * Awaiting approval and Not allocated have none (LB-6087): counting them means reading the
   * company's whole allocation book from the allocation service on every load. Until the summary
   * answers, the company's tabs show none either: the rows in hand are one filtered page of it.
   */
  const visibleTabs = deepLinkBatchId ? BATCH_TABS : BILL_TABS;
  const tabCounts = deepLinkBatchId ? Object.fromEntries(BATCH_TABS.map(tab => [tab.key, scopedRows.filter(row => rowMatchesStatuses(row, tab.statuses)).length])) : (_summaryToTabCounts = summaryToTabCounts(store.companyBills.summary)) !== null && _summaryToTabCounts !== void 0 ? _summaryToTabCounts : {};
  const activeTab = activeTabKey(filters);

  // One page, in the order the service returned it. Nothing is filtered, sorted or sliced here any
  // more: doing any of it over one page would sort the page and call it the list.
  /**
   * What the filter dropdowns offer.
   *
   * Built from the loaded rows until now, which worked only because every row was loaded. Over one
   * page they would quietly shrink to whatever is on screen, so each has its own source: properties
   * from the company's own list, which is where the table reads its addresses too; utilities from
   * the fixed vocabulary the extractor is held to; providers from the service, which reads them the
   * same way the list does and narrows them to what this caller may see.
   */
  const filterOptions = {
    properties: store.company.propertiesList.map(property => ({
      value: property.propertyId,
      label: addressById[property.propertyId] || property.propertyId
    })),
    providers: ((_store$companyBills$f = (_store$companyBills$f2 = store.companyBills.filterOptions) === null || _store$companyBills$f2 === void 0 ? void 0 : _store$companyBills$f2.providers) !== null && _store$companyBills$f !== void 0 ? _store$companyBills$f : []).map(name => ({
      value: name,
      label: name
    })),
    utilities: UTILITY_FILTER_OPTIONS.map(name => ({
      value: name,
      label: name
    }))
  };

  /**
   * The rows on screen.
   *
   * The server answers the Status filter everywhere except under a batch scope, where it is applied
   * here instead so the counts above can still describe the whole acquisition event. That is the
   * one place this page filters rows itself, and it is sound only because the event is read whole.
   */
  const paginatedRows = deepLinkBatchId ? scopedRows.filter(row => rowMatchesStatuses(row, filters.statuses)) : scopedRows;

  /**
   * The service provider list's state, for the filter panel (LB-6085's portal half): on its way, failed, or cut at
   * the service's limit. It used to say "No options available" in all three.
   */
  const filterOptionsRead = store.companyBills.getBillFilterOptionsAsync;
  const providerOptions = {
    isLoading: Boolean(filterOptionsRead.inProgress),
    isFailed: Boolean(filterOptionsRead.error) || Boolean(filterOptionsRead.hasEverBeenRun && !store.companyBills.filterOptions),
    isTruncated: Boolean((_store$companyBills$f3 = store.companyBills.filterOptions) === null || _store$companyBills$f3 === void 0 ? void 0 : _store$companyBills$f3.providersTruncated),
    onRetry: () => filterOptionsRead.run(undefined, [])
  };
  const chips = filterChips(filters, filterOptions, {
    showApPush: isAppfolio
  });
  const totalRows = deepLinkBatchId ? paginatedRows.length : scopeSynced ? store.companyBills.total : 0;

  /**
   * The footer's Download (LB-6033) covers the selected bills, or the Processed bills on this page. The rows count only
   * once they answer the request being made: while another page, filter or sort loads, they are still the last view's.
   * A row's menu and a bill's details download that one bill, through the same flow.
   */
  const isPageCurrent = JSON.stringify(store.companyBills.loadedQuery) === JSON.stringify(requestParams);
  const pageProcessedIds = isPageCurrent ? pageProcessedBillIds(paginatedRows) : [];
  const download = useBillsDownload({
    companyId: billsPageCompanyId(store),
    requestParams,
    billIds: selected.length > 0 ? selected : pageProcessedIds,
    includeApPush: isAppfolio
  });
  const billReadyById = useBillReadyDates(paginatedRows, store.companyBills.items);
  const handleSort = property => {
    const isAsc = orderBy === property && order === 'asc';
    dispatchQuery({
      type: 'SET_SORT',
      order: isAsc ? 'desc' : 'asc',
      orderBy: property
    });
  };

  /** Selects or clears this page's rows without disturbing what was chosen on another. */
  const handleSelectAll = e => {
    setSelectedById(previous => {
      const next = _objectSpread({}, previous);
      paginatedRows.forEach(row => {
        if (e.target.checked) next[row.id] = row;else delete next[row.id];
      });
      return next;
    });
  };
  const handleSelectOne = id => {
    setSelectedById(previous => {
      if (previous[id]) {
        const removed = previous[id],
          rest = _objectWithoutProperties(previous, [id].map(_toPropertyKey));
        return rest;
      }
      const row = paginatedRows.find(candidate => candidate.id === id);
      return row ? _objectSpread(_objectSpread({}, previous), {}, {
        [id]: row
      }) : previous;
    });
  };
  useEffect(() => {
    // Trimmed and collapsed before the wait, so typing a trailing space asks for nothing new.
    const settled = searchInput.trim().replace(/\s+/g, ' ');
    if (settled === query.search) return undefined;
    const timeoutId = setTimeout(() => dispatchQuery({
      type: 'SET_SEARCH',
      search: settled
    }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [searchInput, query.search]);
  const handleChangePage = (_, newPage) => dispatchQuery({
    type: 'SET_PAGE',
    page: newPage
  });
  const handleChangeRowsPerPage = e => dispatchQuery({
    type: 'SET_PAGE_SIZE',
    pageSize: parseInt(e.target.value, 10)
  });
  const handleSearchChange = e => setSearchInput(e.target.value);
  const handleClearSelection = () => setSelectedById({});
  const forgetRow = id => {
    store.uploadedBills.removeUploadedBill(id);
    setSelectedById(previous => {
      const removed = previous[id],
        rest = _objectWithoutProperties(previous, [id].map(_toPropertyKey));
      return removed ? rest : previous;
    });
    if (selectedBillId === id) setSelectedBillId(null);
  };

  /**
   * Deleting a bill archives it (LB-5434): it leaves the list but nothing is removed. Still confirmed
   * first. The service has the last word — it refuses a bill whose services have been allocated or
   * pushed and says which, which is worth reading out.
   */
  const handleDeleteRow = async row => {
    setRowToDelete(null);
    const resp = await store.companyBills.deleteBillAsync.run({
      billId: row.id
    });
    if (!resp) return;
    if (resp.isSuccessful) refreshSummary();
    if (!resp.isSuccessful) {
      toast('error', {
        title: 'Bill not deleted',
        body: [resp.message || 'This bill can no longer be deleted.']
      });
      return;
    }
    forgetRow(row.id);
    // The page is a row short until it is read again, and the bills below it have all shifted up
    // one -- so without this the next page skips whichever bill moved into the gap.
    store.companyBills.refetchCurrent();
    toast('success', {
      body: ["".concat(row.fileName, " deleted.")]
    });
  };

  /**
   * A retried bill is archived and a new one takes its place (LB-5787): the old row goes, and the page
   * is read again so the replacement appears where it belongs.
   */
  const handleRetried = () => {
    if (rowToRetry) forgetRow(rowToRetry.id);
    setRowToRetry(null);
    refreshSummary();
    store.companyBills.refetchCurrent();
  };

  /**
   * Deletes what can be deleted out of the selection and keeps the rest.
   *
   * A bill whose services have been allocated is not the dashboard's to remove — those charges live
   * in the allocation service. Rather than refuse the whole batch for one such bill, the ones that
   * can go are deleted and the ones that cannot are named, so the user learns which and why.
   *
   * Deliberately sequential: `run` cancels the request still in flight on the same async action, so
   * deleting in parallel would abort all but the last.
   */
  const handleDeleteSelected = async () => {
    setIsConfirmingBulkDelete(false);

    // The rows themselves, held since they were ticked. Resolved by id against the loaded rows this
    // silently dropped every selection made on another page -- so a confirmation saying "Delete 5"
    // deleted the three still on screen and said nothing about the other two.
    const chosen = Object.values(selectedById);
    const deleted = [];
    // Bills held on this side never reach the service; the rest can still be refused by it. Each keeps the reason it
    // was kept for, said as the service or the row says it, rather than one guess for all of them.
    const kept = chosen.filter(row => !row.canDelete);
    const reasons = new Set(kept.map(row => row.deleteBlockedReason).filter(Boolean));
    for (const row of chosen.filter(item => item.canDelete)) {
      // eslint-disable-next-line no-await-in-loop
      const resp = await store.companyBills.deleteBillAsync.run({
        billId: row.id
      });
      if (resp !== null && resp !== void 0 && resp.isSuccessful) {
        forgetRow(row.id);
        deleted.push(row);
      } else {
        kept.push(row);
        if (resp !== null && resp !== void 0 && resp.message) reasons.add(resp.message);
      }
    }
    if (deleted.length) {
      // The tiles count bills by status, so a bulk delete moves them exactly as a single one does.
      refreshSummary();
      // And the page is now short by however many went, with everything below shifted up into it.
      store.companyBills.refetchCurrent();
      toast('success', {
        body: ["".concat(deleted.length, " bill").concat(deleted.length === 1 ? '' : 's', " deleted.")]
      });
    }
    if (kept.length) {
      const names = kept.slice(0, 3).map(row => row.fileName);
      const rest = kept.length - names.length;
      toast('warning', {
        title: "".concat(kept.length, " bill").concat(kept.length === 1 ? '' : 's', " kept"),
        body: ["Left in place: ".concat(names.join(', ')).concat(rest > 0 ? " and ".concat(rest, " more") : '', "."), ...(reasons.size === 1 ? [...reasons] : ['Open a bill to see why it was kept.'])]
      });
    }

    // What could not go stays selected, so the reason is still in front of the user.
    setSelectedById(Object.fromEntries(kept.map(row => [row.id, row])));
  };
  const hasSelection = selected.length > 0;
  const selectedRows = Object.values(selectedById);
  // How many of the selection are not on this page, so a bulk action over it is never a surprise.
  const selectedOffPage = selectedRows.filter(row => !paginatedRows.some(onPage => onPage.id === row.id)).length;
  // Counted over this page, not the whole selection: compared against the selection, the header box
  // reads as half-ticked whenever anything is chosen on another page.
  const pageSelectedCount = paginatedRows.filter(row => selectedById[row.id]).length;
  const isSingleSelection = selected.length === 1;
  // Attach is the remediation for exactly two states: a rejected sender, or a property nobody
  // could resolve. Every other status either has a property already or is still moving on its own.
  const canAttachSelected = isSingleSelection && [BILL_STATUSES.rejected, BILL_STATUSES.needsProperty].includes((_selectedRows$ = selectedRows[0]) === null || _selectedRows$ === void 0 ? void 0 : _selectedRows$.status);
  const blockedFromDelete = selectedRows.filter(row => !row.canDelete).length;
  const deletableCount = selectedRows.length - blockedFromDelete;
  /**
   * The same reading the row menu takes, so one bill cannot be pushable in one place and not the other. Read off the
   * row as the page has it now when it is on the page: a selection holds the row as it was when it was picked, and a
   * refresh can since have changed its status or its enrolment.
   */
  const selectedPushRow = isSingleSelection ? (_paginatedRows$find = paginatedRows.find(row => row.id === selectedRows[0].id)) !== null && _paginatedRows$find !== void 0 ? _paginatedRows$find : selectedRows[0] : null;
  const selectedPushState = selectedPushRow ? appfolioPush.getState(selectedPushRow.id, selectedPushRow.extractStatus, selectedPushRow.apPush, selectedPushRow.pushInvoiceNumber) : null;
  const selectedPushHint = selectedPushState ? getAppfolioPushMenuHint(selectedPushState) : null;
  /**
   * What is holding the selection back, read over the rows that are actually held. A mixed selection names every
   * reason that applies rather than whichever came first: being told a bill was billed when a payable is what holds it
   * sends someone looking in the wrong place.
   */
  const heldRows = selectedRows.filter(row => !row.canDelete);
  const selectionDeleteHint = getSelectionDeleteHint(heldRows);
  /** Deleting an allocated bill archives its allocation (LB-5952), which the bulk confirmation says before it happens. */
  const deletesAllocations = selectedRows.some(row => row.canDelete && row.isAllocated);
  /** Some held and some not: the delete is still worth offering, and worth saying it will not take all. */
  const partialDeleteHint = deletableCount > 0 && heldRows.length > 0 ? "".concat(deletableCount, " of ").concat(selectedRows.length, " will be deleted. ").concat(selectionDeleteHint) : null;
  // Counted from the chips, so a filter the page cannot show (AP Push from a link, on a company without it) is not one.
  const hasFiltersOn = Boolean(query.search) || chips.length > 0;
  const hasActiveFilters = hasFiltersOn || Boolean(deepLinkBatchId);
  /** An empty table on first load reads as "no bills", so it waits for the first pass to come back. */
  const isLoadingBills = store.companyBills.getCompanyBillsAsync.inProgress;
  /** What the table says when it has nothing to show, which is now a row in it rather than instead of it. */
  const emptyMessage = deepLinkBatchId && scopedRows.length === 0 ? hasFiltersOn ?
  // The filters narrow the email's bills on the server, so they can hide every one of them.
  'No bills from this email or upload match your search and filters.' : 'No bills from this email or upload are in the list — they may have been deleted since. Clear the filter to see every bill.' : page > 0 ?
  // Past the first page the book is not empty, this page of it is — bills deleted under us,
  // or a total that has moved since. Saying "no bills" here would be plainly untrue, and it
  // is only reachable at all now that an empty page keeps its pagination.
  'Nothing on this page — the list has changed since it was loaded. Go back a page.' : hasActiveFilters ? 'No bills match your search and filters.' : 'No bills uploaded yet.';
  /**
   * How many columns the empty row has to span. Pinned by a test against the header it has to
   * match: a colSpan that falls short leaves the message in a cell one column wide, and nothing
   * else in the build would notice a column being added beside it.
   */
  const columnCount = 17 + (isAppfolio ? 1 : 0);

  // Guards the async refresh below from setting state after the component unmounts (the initial
  // tile-count effect uses its own `active` flag for the same reason).
  const isMountedRef = useRef(true);
  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  /**
   * In-page refresh (LB-5820): re-fetch the list and the summary tiles without a full
   * browser reload. `run` is single-flight, and filters/paging/selection are local state the fetch
   * does not touch, so they survive the refresh.
   */
  const handleRefresh = () => {
    store.companyBills.refetchCurrent();
    refreshSummary();
    setPollRestarts(count => count + 1);
  };
  const renderHeaderFilter = categoryKey => /*#__PURE__*/React.createElement(BillsHeaderFilterButton, {
    categoryKey: categoryKey,
    isActive: pickedValues(filters, categoryKey).length > 0,
    isExpanded: isFilterPanelOpenFrom(categoryKey),
    label: HEADER_FILTER_LABELS[categoryKey],
    onOpen: (anchorEl, key) => handleOpenFilterPanel(anchorEl, key, key)
  });
  return /*#__PURE__*/React.createElement(Box, {
    className: classes.billsRoot
  }, /*#__PURE__*/React.createElement(Tabs, {
    className: classes.billsTabs
    // No tab is lit when the Status and Allocation filters amount to none of them.
    ,
    value: visibleTabs.some(tab => tab.key === activeTab) ? activeTab : false,
    onChange: (event, next) => setFilters(tabFilters(next)),
    variant: "scrollable",
    scrollButtons: false
  }, visibleTabs.map(tab => /*#__PURE__*/React.createElement(Tab, {
    key: tab.key,
    value: tab.key,
    label: /*#__PURE__*/React.createElement("span", {
      className: classes.billsTabLabel
    }, tab.label, typeof tabCounts[tab.key] === 'number' ? /*#__PURE__*/React.createElement("span", {
      className: "".concat(classes.billsTabCount, " ").concat(activeTab === tab.key ? classes.billsTabCountActive : '')
    }, tabCounts[tab.key]) : null)
  }))), /*#__PURE__*/React.createElement(BillsSummaryCards, {
    summary: store.companyBills.summary,
    isLoading: store.companyBills.getCompanyBillsSummaryAsync.inProgress && !store.companyBills.summary
  }), deepLinkBatchId && /*#__PURE__*/React.createElement(BillBatchFilterBanner, {
    count: scopedRows.length,
    onClear: handleClearBatchFilter
  }), /*#__PURE__*/React.createElement(Box, {
    className: classes.billsToolbar
  }, /*#__PURE__*/React.createElement(Box, {
    className: classes.billsToolbarLeft
  }, /*#__PURE__*/React.createElement(InputWithIcon, {
    size: "small",
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "search"
    })
  }, /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    variant: "outlined",
    placeholder: "Search service provider, account #, invoice #, file name or sender",
    fullWidth: true,
    value: searchInput,
    onChange: handleSearchChange,
    inputProps: {
      'aria-label': 'Search bills'
    }
  })), /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    size: "small",
    className: filterClasses.addFilterButton,
    ref: addFilterButtonRef,
    "aria-haspopup": "dialog",
    "aria-expanded": isAnchoredAtAddFilter ? 'true' : 'false',
    "aria-controls": isAnchoredAtAddFilter ? 'bills-filter-panel' : undefined,
    onClick: event => handleOpenFilterPanel(event.currentTarget),
    startIcon: /*#__PURE__*/React.createElement(Icon, {
      type: "plus"
    }),
    'custom-attribute': 'bills-filters'
  }, "Filter"), /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    size: "small",
    className: "".concat(classes.billsRefreshButton).concat(isLoadingBills ? " ".concat(classes.billsRefreshButtonSpinning) : ''),
    onClick: handleRefresh,
    disabled: isLoadingBills,
    "aria-label": "Refresh bills",
    title: "Refresh",
    startIcon: /*#__PURE__*/React.createElement(Icon, {
      type: "reload"
    }),
    'custom-attribute': 'bills-refresh'
  }, "Refresh")), /*#__PURE__*/React.createElement(Box, {
    className: classes.billsToolbarRight
  }, isUserLivableStaff && /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    size: "small",
    disabled: !hasIntakeCompany,
    title: hasIntakeCompany ? 'Manage the bill-intake email address' : 'Select a company to manage its bill-intake address',
    onClick: hasIntakeCompany ? () => setIsIntakeSettingsOpen(true) : undefined,
    'custom-attribute': 'bills-automation-settings'
  }, "Bill automation settings"), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    size: "small",
    startIcon: /*#__PURE__*/React.createElement(Icon, {
      type: "plus"
    }),
    onClick: () => setIsUploadBillModalOpen(true),
    'custom-attribute': 'bills-upload-bill'
  }, uploadsInFlight > 0 ? "Uploading ".concat(uploadsInFlight, "\u2026") : 'Upload bill'))), /*#__PURE__*/React.createElement(BillsFilterChips, {
    chips: chips,
    onClearAll: handleClearAllFilters,
    onOpen: categoryKey => handleOpenFilterPanel(addFilterButtonRef.current, categoryKey, 'chip'),
    onRemove: categoryKey => {
      var _addFilterButtonRef$c;
      setFilters(withCategoryCleared(filters, categoryKey));
      // The chip is gone, and focus with it; it goes to where the next filter would be added.
      (_addFilterButtonRef$c = addFilterButtonRef.current) === null || _addFilterButtonRef$c === void 0 || _addFilterButtonRef$c.focus();
    }
  }), /*#__PURE__*/React.createElement(BillsFilterPanel, {
    anchorEl: filterPanel.anchorEl,
    categoryKey: filterPanel.categoryKey,
    filters: filters,
    onApply: handleApplyFilters,
    onClose: handleCloseFilterPanel,
    openId: filterPanel.openId,
    options: filterOptions,
    providers: providerOptions,
    showApPush: isAppfolio
  }), /*#__PURE__*/React.createElement(Paper, {
    variant: "outlined",
    className: classes.billsTablePaper
  }, /*#__PURE__*/React.createElement(TableContainer, {
    className: classes.billsTableContainer
  }, /*#__PURE__*/React.createElement(Table, {
    stickyHeader: true,
    size: "small",
    className: classes.billsTable,
    "aria-label": "bills table"
  }, /*#__PURE__*/React.createElement(TableHead, null, /*#__PURE__*/React.createElement(TableRow, null, /*#__PURE__*/React.createElement(TableCell, {
    padding: "checkbox",
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsStickyCheckboxHead)
  }, /*#__PURE__*/React.createElement(Checkbox, {
    indeterminate: pageSelectedCount > 0 && pageSelectedCount < paginatedRows.length,
    checked: paginatedRows.length > 0 && pageSelectedCount === paginatedRows.length,
    onChange: handleSelectAll,
    size: "small"
  })), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement("span", {
    className: filterClasses.headerLabel
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'status',
    direction: orderBy === 'status' ? order : 'asc',
    onClick: () => handleSort('status')
  }, "Status"), renderHeaderFilter('statuses'))), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, "Bill ready"), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'received',
    direction: orderBy === 'received' ? order : 'asc',
    onClick: () => handleSort('received')
  }, "Received")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'dueDate',
    direction: orderBy === 'dueDate' ? order : 'asc',
    onClick: () => handleSort('dueDate')
  }, "Due")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement("span", {
    className: filterClasses.headerLabel
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'provider',
    direction: orderBy === 'provider' ? order : 'asc',
    onClick: () => handleSort('provider')
  }, "Service provider"), renderHeaderFilter('providers'))), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement("span", {
    className: filterClasses.headerLabel
  }, "Property", renderHeaderFilter('properties'))), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement("span", {
    className: filterClasses.headerLabel
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'servicePeriod',
    direction: orderBy === 'servicePeriod' ? order : 'asc',
    onClick: () => handleSort('servicePeriod')
  }, "Period"), renderHeaderFilter('servicePeriod'))), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, "Amount"), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'from',
    direction: orderBy === 'from' ? order : 'asc',
    onClick: () => handleSort('from')
  }, "From")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'fileName',
    direction: orderBy === 'fileName' ? order : 'asc',
    onClick: () => handleSort('fileName')
  }, "File name")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement("span", {
    className: filterClasses.headerLabel
  }, "Allocation", renderHeaderFilter('allocationStates'))), isAppfolio ? /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap)
  }, /*#__PURE__*/React.createElement("span", {
    className: filterClasses.headerLabel
  }, "AP Push", renderHeaderFilter('apPushEnrolment'))) : null, /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap, " ").concat(classes.billsSpacedCell)
  }, /*#__PURE__*/React.createElement("span", {
    className: filterClasses.headerLabel
  }, "Utilities", renderHeaderFilter('utilities'))), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap, " ").concat(classes.billsSpacedCell)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'account',
    direction: orderBy === 'account' ? order : 'asc',
    onClick: () => handleSort('account')
  }, "Account #")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap, " ").concat(classes.billsSpacedCell)
  }, "Days"), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsCellNoWrap, " ").concat(classes.billsSpacedCell)
  }, /*#__PURE__*/React.createElement(TableSortLabel, {
    active: orderBy === 'invoiceNumber',
    direction: orderBy === 'invoiceNumber' ? order : 'asc',
    onClick: () => handleSort('invoiceNumber')
  }, "Invoice #")), /*#__PURE__*/React.createElement(TableCell, {
    className: "".concat(classes.tableHeadCell, " ").concat(classes.billsStickyActionsHead),
    align: "center"
  }, "Actions"))), /*#__PURE__*/React.createElement(TableBody, null, paginatedRows.map(row => {
    var _billReadyById$row$id, _billReadyById$row$id2, _row$propertiesLines, _row$propertiesLines2, _row$utilityIcons;
    const pushState = appfolioPush.getState(row.id, row.extractStatus, row.apPush, row.pushInvoiceNumber);
    const appfolioPushChipProps = isAppfolio ? getAppfolioPushChipProps(pushState) : null;
    return /*#__PURE__*/React.createElement(TableRow, {
      key: row.id,
      hover: true,
      className: "".concat(classes.tableBodyRow, " ").concat(classes.billsClickableRow),
      onClick: () => handleViewDetails(row),
      selected: selected.includes(row.id)
    }, /*#__PURE__*/React.createElement(TableCell, {
      padding: "checkbox",
      className: classes.billsStickyCheckbox,
      onClick: e => e.stopPropagation()
    }, /*#__PURE__*/React.createElement(Checkbox, {
      checked: selected.includes(row.id),
      onChange: () => handleSelectOne(row.id),
      size: "small"
    })), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement("span", {
      className: "".concat(classes.billsStatusChip, " ").concat(classes["billsStatus_".concat(billStatusTone(row.status))]),
      title: row.statusTooltip || row.status
    }, /*#__PURE__*/React.createElement("span", {
      className: classes.billsStatusDot
    }), row.status), row.heldChip && /*#__PURE__*/React.createElement(Chip, {
      className: classes.billsTableChip,
      size: "small",
      label: row.heldChip.label,
      variant: row.heldChip.variant,
      title: row.heldChip.title,
      sx: {
        ml: 4
      }
    })), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: ((_billReadyById$row$id = billReadyById[row.id]) === null || _billReadyById$row$id === void 0 ? void 0 : _billReadyById$row$id.title) || undefined
    }, (_billReadyById$row$id2 = billReadyById[row.id]) === null || _billReadyById$row$id2 === void 0 ? void 0 : _billReadyById$row$id2.label)), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.dateUploaded
    }, row.received)), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.dueDate
    }, row.dueDate)), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.provider
    }, row.provider)), /*#__PURE__*/React.createElement(TableCell, {
      className: "".concat(classes.billsPropertiesCell, " ").concat(classes.billsCellNoWrap)
    }, ((_row$propertiesLines = row.propertiesLines) !== null && _row$propertiesLines !== void 0 ? _row$propertiesLines : []).length === 0 ?
    /*#__PURE__*/
    // A bill nobody has placed is the one thing on this row worth acting on,
    // so it says so rather than showing an empty cell.
    React.createElement(BillCellText, {
      noWrap: true,
      className: classes.billsUnmatchedProperty
    }, "\u2014 Choose a property \u2014") : ((_row$propertiesLines2 = row.propertiesLines) !== null && _row$propertiesLines2 !== void 0 ? _row$propertiesLines2 : []).map((line, i) => /*#__PURE__*/React.createElement(BillCellText, {
      key: i,
      noWrap: true,
      title: line,
      sx: {
        lineHeight: 1.35
      }
    }, line))), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.servicePeriod
    }, row.servicePeriod)), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: formatUsd(row.amount)
    }, formatUsd(row.amount))), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.from,
      className: row.senderEmail ? classes.billsSenderEmail : undefined
    }, row.from)), /*#__PURE__*/React.createElement(TableCell, {
      className: "".concat(classes.billsFileNameCell, " ").concat(classes.billsCellNoWrap)
    }, /*#__PURE__*/React.createElement(Box, {
      className: classes.billsFileNameRow
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "file-text",
      state: "grey"
    }), /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.fileName
    }, row.fileName), areNotesShown ? /*#__PURE__*/React.createElement(BillNoteIndicator, {
      note: row.note,
      noteBy: row.noteBy
    }) : null)), /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, /*#__PURE__*/React.createElement(Chip, _extends({
      className: classes.billsTableChip,
      size: "small"
    }, row.allocationChip))), isAppfolio ? /*#__PURE__*/React.createElement(TableCell, {
      className: classes.billsCellNoWrap
    }, appfolioPushChipProps ? /*#__PURE__*/React.createElement(Chip, _extends({
      className: classes.billsTableChip,
      size: "small"
    }, appfolioPushChipProps)) : null) : null, /*#__PURE__*/React.createElement(TableCell, {
      className: "".concat(classes.billsUtilitiesCell, " ").concat(classes.billsCellNoWrap, " ").concat(classes.billsSpacedCell)
    }, (_row$utilityIcons = row.utilityIcons) !== null && _row$utilityIcons !== void 0 && _row$utilityIcons.length ? /*#__PURE__*/React.createElement(Box, {
      className: classes.billsUtilitiesIcons
    }, row.utilityIcons.map((type, uIdx) => {
      var _row$utilityTypes;
      return /*#__PURE__*/React.createElement("span", {
        className: classes.billsUtilityIcon,
        key: "".concat(row.id, "-util-").concat(uIdx),
        title: (_row$utilityTypes = row.utilityTypes) === null || _row$utilityTypes === void 0 ? void 0 : _row$utilityTypes[uIdx]
      }, /*#__PURE__*/React.createElement(Icon, {
        type: type,
        state: "grey"
      }));
    })) : /*#__PURE__*/React.createElement(BillCellText, null, DASH)), /*#__PURE__*/React.createElement(TableCell, {
      className: "".concat(classes.billsCellNoWrap, " ").concat(classes.billsSpacedCell, " ").concat(classes.billsAccountCell)
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.account
    }, row.account)), /*#__PURE__*/React.createElement(TableCell, {
      className: "".concat(classes.billsCellNoWrap, " ").concat(classes.billsSpacedCell)
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: String(row.days)
    }, row.days)), /*#__PURE__*/React.createElement(TableCell, {
      className: "".concat(classes.billsCellNoWrap, " ").concat(classes.billsSpacedCell)
    }, /*#__PURE__*/React.createElement(BillCellText, {
      noWrap: true,
      title: row.invoiceNumber
    }, row.invoiceNumber)), /*#__PURE__*/React.createElement(TableCell, {
      className: "".concat(classes.actionsCell, " ").concat(classes.billsStickyActions),
      align: "center",
      onClick: e => e.stopPropagation()
    }, /*#__PURE__*/React.createElement(BillActionsMenu, {
      canDelete: row.canDelete,
      canPush: pushState.canPush,
      canRetry: !row.retryBlockedReason,
      deleteDisabledHint: row.deleteBlockedReason,
      downloadDisabledHint: billDownloadBlockedReason(row.extractStatus),
      isDownloading: download.isBusy,
      onDelete: () => setRowToDelete(row),
      onDownload: kind => download.start(kind, [row.id]),
      onPush: () => appfolioPush.handlePush(row.id),
      onRetry: () => setRowToRetry(row),
      onViewDetails: () => handleViewDetails(row),
      pushDisabledHint: getAppfolioPushMenuHint(pushState),
      retryDisabledHint: row.retryBlockedReason,
      showPush: canTriggerPush
    })));
  }), paginatedRows.length === 0 && !isLoadingBills && /*#__PURE__*/React.createElement(TableRow, null, /*#__PURE__*/React.createElement(TableCell, {
    colSpan: columnCount,
    className: classes.billsTableEmptyCell
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "span",
    color: "text.secondary",
    className: classes.billsTableEmptyMessage
  }, emptyMessage)))))), /*#__PURE__*/React.createElement(Box, {
    className: classes.billsFooter
  }, /*#__PURE__*/React.createElement(Box, {
    className: classes.billsFooterActions
  }, /*#__PURE__*/React.createElement(BillsDownloadMenu, {
    disabledReason: downloadDisabledReason({
      hasSelection,
      isPageCurrent,
      pageProcessedIds
    }),
    isBusy: download.isBusy,
    onPick: download.start,
    scopeLabel: downloadScopeLabel({
      selectedCount: selected.length,
      pageProcessedCount: pageProcessedIds.length
    })
  }), hasSelection && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(IconButton, {
    onClick: handleClearSelection,
    "aria-label": "Clear selection",
    size: "small"
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "close"
  })), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "span"
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.billsFooterCount
  }, selected.length), " bill", selected.length === 1 ? '' : 's', " selected", selectedOffPage > 0 ? ' (across pages)' : ''), /*#__PURE__*/React.createElement("span", {
    className: classes.billsFooterSeparator
  }, "\xB7"), isSingleSelection &&
  /*#__PURE__*/
  // The reason lives on a wrapper: a disabled MUI button swallows pointer
  // events, so a title on the button itself would never show.
  React.createElement("span", {
    title: canAttachSelected ? 'Attach this bill to a property' : 'Only Rejected or Needs review bills can be attached'
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    size: "small",
    disabled: !canAttachSelected,
    onClick: () => canAttachSelected && setAttachBillId(selectedRows[0].id),
    'custom-attribute': 'bills-selection-attach'
  }, "Attach to property")), isSingleSelection && /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    size: "small",
    onClick: () => selectedRows[0] && handleViewDetails(selectedRows[0]),
    'custom-attribute': 'bills-selection-details'
  }, "Bill details"), isSingleSelection && canTriggerPush &&
  /*#__PURE__*/
  // Disabled rather than hidden, and the reason on a wrapper the pointer can
  // reach — a bill already pushed, or one still on its way, should say so here
  // rather than leave the action looking as though it was never offered.
  React.createElement("span", {
    title: selectedPushHint || undefined
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    size: "small",
    disabled: !(selectedPushState !== null && selectedPushState !== void 0 && selectedPushState.canPush),
    onClick: () => selectedRows[0] && appfolioPush.handlePush(selectedRows[0].id),
    'custom-attribute': 'bills-selection-appfolio-push'
  }, "Push to AppFolio")), /*#__PURE__*/React.createElement("span", {
    title: (deletableCount === 0 ? selectionDeleteHint : partialDeleteHint) || undefined
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "outlined",
    size: "small",
    color: "error"
    // Nothing to offer when every bill chosen is held: opening a confirmation that
    // would delete none of them is worse than saying so here.
    ,
    disabled: deletableCount === 0 || store.companyBills.deleteBillAsync.inProgress,
    onClick: () => setIsConfirmingBulkDelete(true),
    'custom-attribute': 'bills-selection-delete'
  }, "Delete")))), /*#__PURE__*/React.createElement(TablePagination, {
    component: "div",
    count: totalRows,
    page: page,
    onPageChange: handleChangePage,
    rowsPerPage: rowsPerPage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
    className: classes.tablePagination
  }))), /*#__PURE__*/React.createElement(UploadBillModal, {
    isOpen: isUploadBillModalOpen,
    handleClose: () => setIsUploadBillModalOpen(false),
    onOpenBill: handleOpenBillById
  }), isUserLivableStaff && /*#__PURE__*/React.createElement(BillIntakeAliasModal, {
    company: isIntakeSettingsOpen && hasIntakeCompany ? {
      companyId: intakeCompanyId,
      companyName: intakeCompanyName
    } : null,
    onClose: () => setIsIntakeSettingsOpen(false)
  }), /*#__PURE__*/React.createElement(UploadedBillDetailsModal, {
    appfolioPush: appfolioPush,
    bill: selectedBill,
    canTriggerPush: canTriggerPush,
    handleClose: handleCloseDetails,
    isDownloading: download.isBusy
    // The row's verdict, so the view opens with the state the table already showed.
    ,
    listApPush: selectedBillId ? (_store$companyBills$g = (_store$companyBills$g2 = store.companyBills.getBill(selectedBillId)) === null || _store$companyBills$g2 === void 0 ? void 0 : _store$companyBills$g2.apPush) !== null && _store$companyBills$g !== void 0 ? _store$companyBills$g : null : null,
    monthInProgress: monthInProgress,
    onOpenBill: handleOpenBillById,
    onAttach: billId => setAttachBillId(billId),
    onDownload: kind => download.start(kind, [selectedBillId])
    // A saved edit is what the row shows (its invoice number, vendor, amount), so the list is read
    // again as soon as one lands, not only on the way out of the details (LB-5979).
    ,
    onBillChanged: () => store.companyBills.refetchCurrent()
    // Entered by hand, the bill is checked again: its own read, the list and the tiles all follow it (LB-6095).
    ,
    onEnteredByHand: () => {
      store.companyBills.refetchCurrent();
      setDetailReads(count => count + 1);
      setPollRestarts(count => count + 1);
      refreshSummary();
    }
  }), /*#__PURE__*/React.createElement(AttachBillModal
  /*
    Falls back to the bare id when the list cannot answer.
     `getBill` spoke for any bill in the company while the browser held every row. Over one page
    it speaks only for rows some page this session loaded contained, and a bill reached by its
    notification link has never been in one. That matters because the modal reads `row.billId`
    when it submits, so a null row threw there -- after a property had been chosen, not before.
    The id is all it needs; what is lost is the account-number prefill.
     Not the selected row, which is the table’s own shape (`id`, `account`) and not the
    service’s (`billId`, `accountNumber`) -- passing one where the other is expected would
    have attached a bill whose id was undefined.
  */, {
    row: attachBillId ? (_store$companyBills$g3 = store.companyBills.getBill(attachBillId)) !== null && _store$companyBills$g3 !== void 0 ? _store$companyBills$g3 : {
      billId: attachBillId
    } : null,
    isOpen: !!attachBillId,
    handleClose: () => setAttachBillId(null),
    onAttached: () => {
      setAttachBillId(null);
      // The bill re-entered the pipeline (Processing/Extracted), which the list poll waits on --
      // restarted here, because one that gave up would otherwise leave it showing as processing.
      store.companyBills.refetchCurrent();
      setPollRestarts(count => count + 1);
      // The attach is what moved the tiles: the bill leaves Needs review, and ticking "add
      // allowed sender" moves that count too. Waiting for the poller would leave the figures
      // above the table disagreeing with the row that just changed under them.
      refreshSummary();
    }
  }), /*#__PURE__*/React.createElement(ConfirmActionModal, {
    confirmLabel: deletableCount === 1 ? 'Delete bill' : "Delete ".concat(deletableCount, " bills"),
    isOpen: isConfirmingBulkDelete,
    message: (blockedFromDelete === 0 ? "".concat(selected.length, " bill").concat(selected.length === 1 ? '' : 's', " will be removed from your bills. ") + getArchivedNotErasedNote(selected.length) : "".concat(deletableCount, " of ").concat(selected.length, " bills will be removed from your bills. ") + "".concat(getArchivedNotErasedNote(deletableCount), " ") + "The other ".concat(blockedFromDelete, " will stay where ").concat(blockedFromDelete === 1 ? 'it is' : 'they are', ". ") + (selectionDeleteHint || '')) + (deletesAllocations ? ' Allocations made from the bills removed are archived with them, and their tables go back to pending.' : ''),
    onClose: () => setIsConfirmingBulkDelete(false),
    onConfirm: handleDeleteSelected,
    title: selected.length === 1 ? 'Delete bill?' : "Delete ".concat(selected.length, " bills?")
  }), /*#__PURE__*/React.createElement(ConfirmActionModal, {
    confirmLabel: (_download$dialog$conf = (_download$dialog = download.dialog) === null || _download$dialog === void 0 ? void 0 : _download$dialog.confirmLabel) !== null && _download$dialog$conf !== void 0 ? _download$dialog$conf : '',
    isOpen: Boolean(download.dialog),
    message: (_download$dialog$mess = (_download$dialog2 = download.dialog) === null || _download$dialog2 === void 0 ? void 0 : _download$dialog2.message) !== null && _download$dialog$mess !== void 0 ? _download$dialog$mess : '',
    onClose: download.cancel,
    onConfirm: download.confirm,
    title: (_download$dialog$titl = (_download$dialog3 = download.dialog) === null || _download$dialog3 === void 0 ? void 0 : _download$dialog3.title) !== null && _download$dialog$titl !== void 0 ? _download$dialog$titl : ''
  }), /*#__PURE__*/React.createElement(RetryBillModal, {
    handleClose: () => setRowToRetry(null),
    isOpen: Boolean(rowToRetry),
    onRetried: handleRetried,
    row: rowToRetry
  }), /*#__PURE__*/React.createElement(ConfirmActionModal, {
    confirmLabel: "Delete bill",
    isOpen: Boolean(rowToDelete),
    message: getDeleteConfirmation({
      fileName: rowToDelete === null || rowToDelete === void 0 ? void 0 : rowToDelete.fileName,
      allocationCount: rowToDelete === null || rowToDelete === void 0 ? void 0 : rowToDelete.allocationCount
    }),
    onClose: () => setRowToDelete(null),
    onConfirm: () => handleDeleteRow(rowToDelete),
    title: "Delete bill?"
  }));
});